/**
 * evidenceIntake — thin client for the Phase A backend truth layer.
 *
 * The UI must reflect backend truth, not become a second source of
 * truth. Every helper below is a straight passthrough to a
 * doctrine-pinned endpoint. Canonical vocabulary is re-exported here
 * for the intake modal — do not extend or rename these arrays; every
 * value MUST match `backend/evidence.py` enums exactly.
 */
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Canonical vocabulary mirrors (backend/evidence.py) ─────────────
export const SUBJECT_TYPES = Object.freeze([
  "self", "existing_pupil", "new_pupil", "pdi", "adi", "other",
]);
export const PURPOSES = Object.freeze([
  "lesson_review", "voice_reflection", "mock_test", "driving_assessment",
  "reflection", "homework_generation", "part3_support",
  "incident_review", "general_coaching", "other",
]);
export const RECIPIENTS = Object.freeze([
  "uploader", "pupil", "supervisor", "organisation", "admin", "other",
]);
export const PLATFORM_ACTIONS = Object.freeze([
  "update_passport", "generate_homework", "update_progress",
  "notify_recipients", "create_reflection",
]);

// ── Read-side helpers ──────────────────────────────────────────────
export async function listMyEvidence({ status, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (limit) params.set("limit", String(limit));
  const url = `${API}/api/evidence/me${params.toString() ? `?${params}` : ""}`;
  const { data } = await axios.get(url, { withCredentials: true });
  return data; // { count, evidence: [...] }
}

export async function listMyPendingActions({ limit = 100 } = {}) {
  const { data } = await axios.get(
    `${API}/api/evidence/pending-actions/me?limit=${limit}`,
    { withCredentials: true },
  );
  return data; // { count, pending_actions: [...] } (oldest first)
}

// ── Write-side helpers ─────────────────────────────────────────────
export async function completeIntake(evidenceId, payload) {
  const { data } = await axios.post(
    `${API}/api/evidence/${evidenceId}/intake`,
    payload,
    { withCredentials: true },
  );
  return data;
}

export async function finalizeVoiceReflection({ upload_id, lesson_id }) {
  const { data } = await axios.post(
    `${API}/api/evidence/voice-reflection/finalize`,
    { upload_id, lesson_id },
    { withCredentials: true },
  );
  return data; // { evidence_id, lesson_id, evidence_status, lifecycle_status }
}

// ── Presentation helpers ───────────────────────────────────────────
export function humanizeAge(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return `${sec} second${sec === 1 ? "" : "s"} ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? "" : "s"} ago`;
}

// ── Calm label lookups (UI copy only, never sent to backend) ───────
export const ACTION_TYPE_LABEL = Object.freeze({
  evidence_intake_incomplete: "Complete evidence intake",
  lesson_reflection_missing:  "Lesson reflection outstanding",
  homework_awaiting_approval: "Homework awaiting approval",
  analysis_awaiting_review:   "Analysis awaiting review",
  notification_not_sent:      "Notification not sent",
});

export const ACTION_TYPE_CONTEXT = Object.freeze({
  evidence_intake_incomplete: (row) => (
    row?.detail?.upload_id ? "Uploaded" : "Evidence created"
  ),
  lesson_reflection_missing:  () => "Lesson ended",
  homework_awaiting_approval: () => "Draft ready",
  analysis_awaiting_review:   () => "Analysis ready",
  notification_not_sent:      () => "Queued",
});
